import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, IconButton, CircularProgress
} from '@mui/material';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import s3Utils from '../../../utils/s3Utils';
import { generateDecisionLetter } from "../../../components/certificate/DecisionLetterGenerator";
import dayjs from "dayjs";
import { encryptAppId, encryptText } from '../../../utils/encryption';

const ILACAppDashboard = () => {
  const [activeTab, setActiveTab] = useState('New');
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState({ New: 0, Accepted: 0, Rejected: 0 });

  // Modal states
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [decisionDialogOpen, setDecisionDialogOpen] = useState(false);
  const [trailsDialogOpen, setTrailsDialogOpen] = useState(false);
  const [forwardDialogOpen, setForwardDialogOpen] = useState(false);

  // Form states
  const [selectedApp, setSelectedApp] = useState(null);
  const [acceptPassword, setAcceptPassword] = useState('');
  const [rejectPassword, setRejectPassword] = useState('');
  const [rejectRemarks, setRejectRemarks] = useState('');
  const [forwardPassword, setForwardPassword] = useState('');
  const [selectedDirector, setSelectedDirector] = useState('0');
  const [directorsList, setDirectorsList] = useState([]);

  useEffect(() => {
    loadApplications();
    loadAllCounts();
  }, [activeTab]);

  const loadApplications = async () => {
    try {
      setLoading(true);
      const orgId = localStorage.getItem('orgId');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/final-submit-additional/jd/applications-ilac`,
        { params: { orgid: orgId, appType: activeTab } }
      );
      if (response.data.success) setApplications(response.data.data);
    } catch (error) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const loadAllCounts = async () => {
    try {
      const orgId = localStorage.getItem('orgId');
      const tabs = ['New', 'Accepted', 'Rejected'];
      const results = await Promise.all(
        tabs.map(tab =>
          axios.get(`${process.env.REACT_APP_API_URL}/final-submit-additional/jd/applications-ilac`, {
            params: { orgid: orgId, appType: tab }
          })
        )
      );
      const newCounts = {};
      results.forEach((res, idx) => {
        newCounts[tabs[idx]] = res.data.data?.length || 0;
      });
      setCounts(newCounts);
    } catch (error) {
      console.error(error);
    }
  };

  const handleAcceptClick = (app) => {
    setSelectedApp(app);
    setAcceptDialogOpen(true);
  };

  const handleRejectClick = (app) => {
    setSelectedApp(app);
    setRejectDialogOpen(true);
  };

  const handleForwardClick = async (app) => {
    setSelectedApp(app);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/final-submit-additional/ceolist`, {
        params: { appid: app.appid, usertypeid: 8 }
      });
      setDirectorsList(res.data.ceo || []);
      setForwardDialogOpen(true);
    } catch (err) {
      toast.error('Failed to load directors');
    }
  };

  const handleViewDecision = (app) => {
      generateDecisionLetterPDF(app);
  };

  const generateDecisionLetterPDF = async (data) => {
    try {
      toast.info('Generating decision letter...');
      const letterData = {
        applicationNo: data.ApplicationNo || "N/A",
        letterDate: data.DecisionDate || "N/A",
        contactPerson: data.ContactPerson || "N/A",
        laboratoryName: data.LabName || "N/A",
        address: data.Address || "N/A",
        extAddress: data.Extaddress || "N/A",
        mobile: data.ContactMobile || "N/A",
        email: data.ContactEmail || "N/A",
        subject: data.Subject || "N/A",
        body: data.body || data.Body || "N/A",
        doName: data.Name || "N/A",
        doEmail: data.doEmail || "N/A",
        showNewAddress: new Date() > new Date("2025-07-18"),
      };
      await generateDecisionLetter(letterData);
      toast.success('Decision letter downloaded');
    } catch (err) {
      toast.error('Failed to generate decision letter');
    } finally {
      toast.dismiss();
    }
  };

  const handleViewFile = async (filePath) => {
    if (!filePath || filePath === 'N/A') return toast.error('File not found');
    await s3Utils.handleFileViewWithS3(filePath, {
      showError: (msg) => toast.error(msg),
      expiresIn: 172800,
    });
  };

  const handleAcceptSubmit = async () => {
    if (!acceptPassword.trim()) return toast.warning('Please enter your password');
    try {
      setLoading(true);
      const orgId = localStorage.getItem('orgId');
      const pwdRes = await axios.post(`${process.env.REACT_APP_API_URL}/auth/verify-password`, {
        orgId, password: acceptPassword
      });
      if (!pwdRes.data.success) return toast.error('Invalid password');

      await axios.post(`${process.env.REACT_APP_API_URL}/final-submit-additional/jd/accept-ilac`, {
        id: selectedApp.id,
        IsApproved: 1,
        orgid: orgId,
        Remarks: '',
        role: 9,
        appid: selectedApp.appid
      });
      toast.success('Application approved successfully');
      setAcceptDialogOpen(false);
      setAcceptPassword('');
      loadApplications();
      loadAllCounts();
    } catch (err) {
      toast.error('Failed to approve application');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectPassword.trim() || !rejectRemarks.trim()) {
      return toast.warning('Password and remarks are required');
    }
    try {
      setLoading(true);
      const orgId = localStorage.getItem('orgId');
      const pwdRes = await axios.post(`${process.env.REACT_APP_API_URL}/auth/verify-password`, {
        orgId, password: rejectPassword
      });
      if (!pwdRes.data.success) return toast.error('Invalid password');

      await axios.post(`${process.env.REACT_APP_API_URL}/final-submit-additional/jd/reject-ilac`, {
        id: selectedApp.id,
        IsApproved: 2,
        orgid: orgId,
        Remarks: rejectRemarks,
        role: 9,
        Type: 'ILAC'
      });
      toast.success('Application disapproved successfully');
      setRejectDialogOpen(false);
      setRejectPassword('');
      setRejectRemarks('');
      loadApplications();
      loadAllCounts();
    } catch (err) {
      toast.error('Failed to reject application');
    } finally {
      setLoading(false);
    }
  };

  const handleForwardSubmit = async () => {
    if (!forwardPassword.trim() || selectedDirector === '0') {
      return toast.warning('Select director and enter password');
    }
    try {
      const orgId = localStorage.getItem('orgId');
      const pwdRes = await axios.post(`${process.env.REACT_APP_API_URL}/auth/verify-password`, {
        orgId, password: forwardPassword
      });
      if (!pwdRes.data.success) return toast.error('Invalid password');

      await axios.post(`${process.env.REACT_APP_API_URL}/final-submit-additional/jd/forward-ilac`, {
        id: selectedApp.id,
        sentto: selectedDirector,
        orgid: orgId
      });
      toast.success('Application forwarded successfully');
      setForwardDialogOpen(false);
      loadApplications();
    } catch (err) {
      toast.error('Failed to forward');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Permission to use NABL Accredited CAB combined ILAC MRA Mark
      </Typography>
      <hr />

      {/* Three buttons exactly as in ASPX */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3 }}>
        <Button variant={activeTab === 'New' ? 'contained' : 'outlined'} onClick={() => setActiveTab('New')} sx={{ height: 50 }}>
          New Application ({counts.New})
        </Button>
        <Button variant={activeTab === 'Accepted' ? 'contained' : 'outlined'} onClick={() => setActiveTab('Accepted')} sx={{ height: 50 }}>
          Approved Application ({counts.Accepted})
        </Button>
        <Button variant={activeTab === 'Rejected' ? 'contained' : 'outlined'} onClick={() => setActiveTab('Rejected')} sx={{ height: 50 }}>
          Disapproved Application ({counts.Rejected})
        </Button>
      </Box>

      {/* Table with all columns as per ASPX */}
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Action</TableCell>
              <TableCell>Sr. No.</TableCell>
              <TableCell>Lab Id</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Forward By</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Complaint Details</TableCell>
              <TableCell align="center">View Decision</TableCell>
              <TableCell>Remarks</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={11} align="center"><CircularProgress /></TableCell></TableRow>
            ) : applications.length === 0 ? (
              <TableRow><TableCell colSpan={11} align="center">No applications found</TableCell></TableRow>
            ) : (
              applications.map((app, idx) => (
                <TableRow key={app.id}>
                  <TableCell>
                    {app.VisBtn === 1 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Button size="small" variant="contained" color="success" onClick={() => handleAcceptClick(app)}>
                          Approve
                        </Button>
                        <Button size="small" variant="contained" color="error" onClick={() => handleRejectClick(app)}>
                          Disapprove
                        </Button>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>{app.ApplicationNo}</TableCell>
                  <TableCell>{app.LoginName}</TableCell>
                  <TableCell>{app.Name}</TableCell>
                  <TableCell>{app.Category}</TableCell>
                  {/* Complaint Details */}
                  <TableCell>
                    <Typography variant="body2"><strong>Complaint:</strong> {app.IsComplaint}</Typography>
                    <Typography variant="body2"><strong>Complaint Remarks Cell and Additional information (IF Any):</strong> {app.CCell_Remarks}</Typography>
                  </TableCell>
                  {/* View Decision (eye icon) */}
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleViewDecision(app)}>
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                  {/* Remarks */}
                  <TableCell>
                    <Typography variant="body2">{app.ApproveRemarks}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Do Remarks Additional information (IF Any):</strong> {app.Do_Remarks}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Accept Modal */}
      <Dialog open={acceptDialogOpen} onClose={() => setAcceptDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth margin="normal" type="password" label="Enter Your Password to Continue"
            value={acceptPassword} onChange={(e) => setAcceptPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAcceptDialogOpen(false)} color="error">Cancel</Button>
          <Button onClick={handleAcceptSubmit} variant="contained" disabled={loading}>Submit</Button>
        </DialogActions>
      </Dialog>

      {/* Disapprove Modal */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Disapprove</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="normal" label="Enter Disapprove Remarks" multiline rows={3}
            value={rejectRemarks} onChange={(e) => setRejectRemarks(e.target.value)}
          />
          <TextField fullWidth margin="normal" type="password" label="Enter Your Password to Continue"
            value={rejectPassword} onChange={(e) => setRejectPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} color="error">Cancel</Button>
          <Button onClick={handleRejectSubmit} variant="contained" disabled={loading}>Submit</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ILACAppDashboard;